import React, { CSSProperties, FC, ReactNode, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../../../../../hooks/useUser";
import { isInternal } from "../../../../../utils";
import { IconGoToTabGlyph } from "../../../videos/selectedVideoCardV2/panels";
import { urlHostPath } from "../../../videos/selectedVideoCardV2/performance/pageScore";
import type { ISingleProduct } from "../../../productsV2/productListView/constants";
import { productHeader, productUrlHref } from "../pageModel";
import { CARD_SHADOW } from "./SectionCard";
import { glyph, MICRO_STYLE } from "./ui";

/**
 * The product header (handoff §1): image · back row (back + ⋮) · h1 ·
 * brands · 2-line description · meta card.
 *
 * The pieces are the live video header's (VID/Header.tsx): its back button
 * (:222-232), its h1 classes (:233-238), its meta card and gold micro-labels
 * (:62-74, :281-288). `font-CircularStdBlack` renders Book (D2), so the title
 * reads as the handoff's Book 400 without asking for it.
 *
 * The photo has no plate: no border, padding, fill or shadow around it, only
 * the 12px radius the plate had (user, 2026-09-28); a --track tile stands in
 * while it downloads. From sm it sits in a square box matched to the column
 * beside it, within PLATE_MIN–PLATE_MAX (usePlateSide).
 */

// ---------------------------------------------------------------------------
// Layout — shared with the loading skeleton (PageStates.tsx), so the two use
// the same slots. The image box's side follows each one's own column
// (usePlateSide), so it changes by the difference when the product lands.
// ---------------------------------------------------------------------------

/**
 * One grid, two layouts, one DOM order (so the ⋮ menu mounts once). Below sm
 * the back row, the image box (full width, 200px) and the identity column
 * stack. From sm the box spans both rows on the left, `--plate` wide and
 * tall, set by usePlateSide (PLATE_MIN until it runs). The rows are
 * `auto 1fr`: when the square is taller than the column beside it (a column
 * under PLATE_MIN), the extra height goes to the identity row, never between
 * the back row and the title.
 */
export const HEADER_GRID =
  "grid grid-cols-1 items-start gap-3 sm:gap-x-6 sm:grid-cols-[var(--plate,180px)_minmax(0,1fr)] sm:grid-rows-[auto_1fr]";
export const SLOT_BACK = "flex items-center justify-between gap-3 min-w-0 sm:col-start-2 sm:row-start-1";
/**
 * The image box: a frame for sizing only (nothing painted), with the photo,
 * the no-image tile or the skeleton centred in it. `min-h-0` +
 * `overflow-hidden` keep a large packshot from growing it.
 */
export const SLOT_PLATE =
  "flex items-center justify-center min-h-0 overflow-hidden h-[200px] sm:h-[var(--plate,180px)] sm:col-start-1 sm:row-start-1 sm:row-span-2";
export const SLOT_IDENTITY = "min-w-0 flex flex-col gap-3 sm:col-start-2 sm:row-start-2";
/**
 * The live meta card (VID/Header.tsx:281-288), as a <dl>, with the handoff's
 * padding (16/20) and gap (16/28) from README §1 in place of the live 16 and
 * 16/20. Label-to-value (8px) and line heights stay the live card's.
 */
export const META_CARD =
  "m-0 bg-surface border border-border-token rounded-xl grid grid-cols-1 sm:grid-cols-2 items-start";
export const META_CARD_STYLE = { padding: "16px 20px", gap: "16px 28px", ...CARD_SHADOW } as const;

/**
 * The square's side, clamped. 180 is the floor, under the shortest real
 * column (one-line title, one brand row, no description or URL, one meta row:
 * 197px), so every real column lines up; it only keeps a header with no brand
 * row (165px) near the 200px the photo has below sm. 340 is the ceiling: the
 * tallest common column at desktop widths (2-line title, 2-line description,
 * two meta rows) measures 340, so it still lines up, and a taller column
 * keeps a 340 photo, top-aligned. TEXT_MIN keeps the column beside it
 * readable (the meta card's two cells ~136px each): on a grid under 704px
 * (viewports 640–767, and 900–1015 where the side nav opens) the square can
 * stop short of the column (224 at 900) rather than squeeze the text.
 */
const PLATE_MIN = 180;
const PLATE_MAX = 340;
const TEXT_MIN = 340;
/** A settle's passes: 2–3 in practice (measure, re-measure after a wrap, confirm). */
const MAX_PASSES = 4;

/**
 * Sizes the image box from sm: a square whose side is the height of the
 * column beside it, back row top to meta card bottom, clamped to
 * [PLATE_MIN, min(PLATE_MAX, grid width − gap − TEXT_MIN)]. CSS can't size a
 * column from a sibling's height, so the column is measured and the side
 * written to the grid's `--plate` (no React state, so no re-render).
 *
 * The side feeds back: a wider square narrows the column, which can wrap a
 * line and make it taller. The column only grows as the square does, so
 * iterating side → height from PLATE_MIN only ever climbs and stops at the
 * smallest side that fits (2–3 passes in practice, MAX_PASSES at most). Each
 * settle runs synchronously in one frame, so no in-between size is painted,
 * and always from PLATE_MIN, so a width gets the same side however it was
 * reached (no drift across resizes). It runs again only when the grid's width
 * or the column's height differ from what its own last write left, so its
 * own write never retriggers it: it cannot oscillate. The ResizeObserver
 * only schedules a frame; resizing inside its callback would raise "loop
 * completed with undelivered notifications".
 *
 * Below sm (one grid column) it clears `--plate`: the box is 200px tall.
 * Shared with the skeleton, whose box then matches its own bars' column.
 */
export const usePlateSide = <T extends HTMLElement>() => {
  const gridRef = useRef<T>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const identityRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    const back = backRef.current;
    const identity = identityRef.current;
    if (!grid || !back || !identity) return undefined;

    const column = () => identity.getBoundingClientRect().bottom - back.getBoundingClientRect().top;
    const write = (side: number | null) =>
      side == null ? grid.style.removeProperty("--plate") : grid.style.setProperty("--plate", `${side}px`);
    // What the last settle left: "grid width:column height".
    let settled = "";
    let frame = 0;

    const settle = () => {
      frame = 0;
      const css = getComputedStyle(grid);
      // One track: the stacked layout.
      if (css.gridTemplateColumns.trim().split(/\s+/).length < 2) {
        write(null);
        settled = "";
        return;
      }
      const width = grid.clientWidth;
      const state = () => `${width}:${column().toFixed(1)}`;
      if (state() === settled) return;
      const max = Math.max(PLATE_MIN, Math.min(PLATE_MAX, width - (parseFloat(css.columnGap) || 0) - TEXT_MIN));
      // Unrounded, so the side lands on the column's exact height.
      const fit = (height: number) => Math.min(max, Math.max(PLATE_MIN, height));
      let side = PLATE_MIN;
      write(side);
      for (let pass = 0; pass < MAX_PASSES; pass += 1) {
        const next = fit(column());
        if (Math.abs(next - side) < 0.5) break;
        side = next;
        write(side);
      }
      settled = state();
    };

    settle(); // before the first paint
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      if (!frame) frame = requestAnimationFrame(settle);
    });
    // The column's width follows the grid's, so this sees window resizes too.
    ro.observe(back);
    ro.observe(identity);
    return () => {
      ro.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return { gridRef, backRef, identityRef };
};

// The live meta label (VID/Header.tsx:62-63,67): gold, 9.5px, .12em (ui.tsx
// MICRO_STYLE). Gold here, where the page's Micro is --text-2.
const MICRO = "font-CircularStdBlack uppercase";

/**
 * The live 32px back control (VID/Header.tsx:222-232), 44px below md. Also
 * rendered by the skeleton, so back is reachable while the product loads.
 */
export const BackButton: FC<{ onClick: () => void; label?: string }> = ({ onClick, label = "Back to products" }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="w-8 h-8 max-md:w-11 max-md:h-11 shrink-0 border border-border-token rounded-lg bg-surface text-text-2 inline-flex items-center justify-center hover:text-text-1 hover:border-accent transition-colors"
  >
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m14 6-6 6 6 6" />
    </svg>
  </button>
);

const MetaCell: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <div className="min-w-0">
    <dt className={`${MICRO} text-accent-text`} style={MICRO_STYLE}>
      {label}
    </dt>
    <dd className="m-0 min-w-0" style={{ marginTop: 8, fontSize: 13.5 }}>
      {children}
    </dd>
  </div>
);

/** The h1's id: the page's <article> is labelled by it (index.tsx). */
export const PRODUCT_TITLE_ID = "product-title";

const EmptyValue: FC = () => <span className="text-muted">—</span>;

/** The outline chip after a retail network's name (handoff §1, "NETWORK"). */
const NetworkChip: FC = () => (
  <span
    className="inline-flex items-center h-[18px] px-[7px] rounded-full border border-border-token text-text-2 uppercase whitespace-nowrap shrink-0"
    style={{ fontSize: 10, letterSpacing: ".12em" }}
  >
    Network
  </span>
);

/** The box is a size container, so the photo can fit itself with cq units. */
const PLATE_BOX_STYLE: CSSProperties = { containerType: "size" };

/**
 * The photo, whole (never cropped) and as large as the box allows, its own
 * corners rounded. The <img> is sized to the photo, not the box: with
 * `object-contain` on a full-box <img> the radius would round the box's
 * corners while a non-square photo sat inside with square ones. Three in four
 * product images are square (local DB, 282 of 377 with a brand) and fill the
 * box; a portrait one fills its height, a landscape one its width, centred.
 * Below sm a square photo is 200px, centred in the full-width box. Until the
 * photo loads it is a square --track tile, the skeleton's colour, so the box
 * is never empty; the fill goes once it loads, so a transparent photo sits on
 * the page, not on a tint. `max-w/h-full` fit it where cq units aren't
 * supported. No image, or one that fails to load, shows a borderless
 * --surface tile (--text-2 on it is 4.8:1) instead of a broken glyph.
 */
const ImagePlate: FC<{ src: string | null; title: string }> = ({ src, title }) => {
  const [failed, setFailed] = useState<string | null>(null);
  // The loaded photo's ratio, for the src it was read from.
  const [ratio, setRatio] = useState<{ src: string; value: number } | null>(null);
  const show = src && failed !== src;
  const loadedRatio = ratio && ratio.src === src ? ratio.value : null;
  const r = loadedRatio ?? 1;
  return (
    <div className={SLOT_PLATE} style={PLATE_BOX_STYLE}>
      {show ? (
        <img
          src={src}
          alt={title}
          onError={() => setFailed(src)}
          onLoad={(e) => {
            const { naturalWidth, naturalHeight } = e.currentTarget;
            // No intrinsic size (an SVG without one): it stays square.
            setRatio({ src, value: naturalWidth && naturalHeight ? naturalWidth / naturalHeight : 1 });
          }}
          className={`block max-w-full max-h-full rounded-xl object-contain${loadedRatio == null ? " bg-track" : ""}`}
          style={{ width: `min(100cqw, ${r} * 100cqh)`, aspectRatio: `${r}` }}
        />
      ) : (
        <div className="h-full aspect-square max-w-full rounded-xl bg-surface flex flex-col items-center justify-center gap-2 text-text-2">
          <span className="inline-flex" aria-hidden="true">
            {glyph("package", 36, 1.5)}
          </span>
          <span style={{ fontSize: 11.5 }}>No product image</span>
        </div>
      )}
    </div>
  );
};

interface Props {
  product: ISingleProduct;
  /** Go back (the page decides where). No button without it, as live. */
  onBack?: () => void;
  /** The back button's name, "Back to products" unless given. */
  backLabel?: string;
  /** The ⋮ at the right of the back row (ProductHeaderMenu). */
  menu?: ReactNode;
}

export const ProductHeader: FC<Props> = ({ product, onBack, backLabel, menu }) => {
  const { role } = useUser();
  const internal = isInternal(role);
  const view = useMemo(() => productHeader(product, internal), [product, internal]);
  const href = productUrlHref(view.url);
  const { gridRef, backRef, identityRef } = usePlateSide<HTMLElement>();

  return (
    // Inside the page's <article>, so this is the product's header, not a banner.
    <header ref={gridRef} className={HEADER_GRID}>
      <div ref={backRef} className={SLOT_BACK}>
        {onBack && <BackButton onClick={onBack} label={backLabel} />}
        {menu && <div className="ml-auto inline-flex shrink-0">{menu}</div>}
      </div>

      <ImagePlate src={view.imageUrl} title={view.title} />

      <div ref={identityRef} className={SLOT_IDENTITY}>
        <h1
          id={PRODUCT_TITLE_ID}
          className="m-0 font-CircularStdBlack text-text-1 text-[24px] sm:text-[28px] break-words"
          style={{ lineHeight: 1.15 }}
        >
          {view.title}
        </h1>

        {view.brands.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-text-2 min-w-0" style={{ fontSize: 13.5 }}>
            {view.brands.map((brand, i) => (
              <React.Fragment key={brand.id}>
                {i > 0 && (
                  <>
                    <span className="sr-only">, </span>
                    <span aria-hidden="true" className="text-muted">
                      ·
                    </span>
                  </>
                )}
                <span className="inline-flex items-center gap-1.5 min-w-0 break-words">
                  {brand.name}
                  {brand.network && <NetworkChip />}
                </span>
              </React.Fragment>
            ))}
          </div>
        )}

        {view.description && (
          <p
            className="m-0 text-text-2 line-clamp-2 break-words max-w-[72ch]"
            style={{ fontSize: 13.5, lineHeight: 1.5 }}
            title={view.description}
          >
            {view.description}
          </p>
        )}

        <dl className={META_CARD} style={META_CARD_STYLE}>
          <MetaCell label="Category">
            {view.category ? <span className="text-text-1 break-words">{view.category}</span> : <EmptyValue />}
          </MetaCell>

          {view.url && (
            <MetaCell label="Product URL">
              {href ? (
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  title={view.url}
                  className="flex items-center gap-1.5 min-w-0 text-text-1 hover:text-accent-text transition-colors"
                >
                  {/* From href, so "//host/p" reads "host/p" like any other address. */}
                  <span className="truncate">{urlHostPath(href)}</span>
                  <span className="inline-flex shrink-0 text-text-2">
                    <IconGoToTabGlyph size={13} strokeWidth={1.8} />
                  </span>
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              ) : (
                // Not a web address (another scheme, a site path, a product
                // name): shown, never linked.
                <span className="block truncate text-text-1" title={view.url}>
                  {view.url}
                </span>
              )}
            </MetaCell>
          )}

          <MetaCell label="Created">
            {/* Proportional figures: tabular-nums widens CircularStd's space and
                comma ("Oct 4, 2022" 83px vs 71px). Tabular is Product ID only (README:72). */}
            {view.created ? <span className="text-text-1">{view.created}</span> : <EmptyValue />}
          </MetaCell>

          {/* Staff only (isInternal). Today's header line showed it to every
              role; the plan narrows it ("Nothing lost", first row). */}
          {view.productId != null && (
            <MetaCell label="Product ID">
              <span className="text-text-1 tabular-nums">{view.productId}</span>
            </MetaCell>
          )}
        </dl>
      </div>
    </header>
  );
};
